import { Box, Link, Typography } from 'components';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import {
  StyledSidebar,
  StyledSidebarItem,
  MenuNextIcon,
  StyledDivider,
  StyledButtonLogInOut,
} from './style';
import { LogoIcon } from 'apps/company/components/Icon';
import { useLocation, useNavigate } from 'react-router-dom';
import RouteName from 'config/routes';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import companyUserSelector from 'selectors/companyUserSelector';
import campaignNotificationSelector from 'selectors/campaignNotificationSelector';
import latestSystemNotificationSelector from 'selectors/latestSystemNotificationSelector';
import { resetAuthCompanyAction } from 'reducers/authCompanyReducer';
// import moment from 'moment';
import { authCompanyApi, useUpdateStatusNotificationMutation } from 'services/authCompany';
import { branchApi } from 'services/company/branch';
import { companyUserApi } from 'services/company/company_user';
import { infoApi } from 'services/company/info';
import { notificationApi } from 'services/company/notification';
import { systemSettingApi } from 'services/company/systemSetting';
import { trainingsApi } from 'services/company/training';
import { usersApi } from 'services/company/user';
import ConfirmModal from 'components/ConfirmModal';

const COMPANY_API_RESET_ACTIONS = [
  authCompanyApi.util.resetApiState,
  branchApi.util.resetApiState,
  companyUserApi.util.resetApiState,
  infoApi.util.resetApiState,
  notificationApi.util.resetApiState,
  systemSettingApi.util.resetApiState,
  trainingsApi.util.resetApiState,
  usersApi.util.resetApiState,
];

const Sidebar = forwardRef(({ content, children, ...props }, ref) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const [currentPath, setCurrentPath] = useState();
  const user = useSelector(companyUserSelector);
  const campaignNotification = useSelector(campaignNotificationSelector);
  const latestSystemNotification = useSelector(
    latestSystemNotificationSelector,
  );
  const [updateStatusNotification] = useUpdateStatusNotificationMutation();
  // const [updateAllStatusNotification] = useUpdateAllStatusNotificationMutation();

  const [
    showConfirmLeaveRequiredTraining,
    setShowConfirmLeaveRequiredTraining,
  ] = useState(false);
  const [actionType, setActionType] = useState(null);

  const [checkDisplay, setCheckDisplay] = useState(
    user ? user.status_notification : false,
  );

  const handleViewSystemNotification = useCallback(
    async (shouldCheckRequiredTraining) => {
      const isEditingRequiredTraining = JSON.parse(
        sessionStorage.getItem('isEditingRequiredTraining') || false,
      );

      if (shouldCheckRequiredTraining && isEditingRequiredTraining) {
        setShowConfirmLeaveRequiredTraining(true);
        setActionType('systemNotification');
        return;
      }

      if (user && user.status_notification === true) {
        try {
          const response = await updateStatusNotification(
            latestSystemNotification?.id,
          );
          if (response.data.success) {
            setCheckDisplay(false);
          } else {
            setCheckDisplay(true);
          }
        } catch (error) {
          console.error(error);
        }
      }
      navigate(RouteName.systemNotification);
    },
    [latestSystemNotification?.id, navigate, updateStatusNotification, user],
  );

  // useEffect(() => {
  //   const isDisplay = latestSystemNotification != null && moment().diff(moment(latestSystemNotification?.published_at), 'day') <= 21
  //   console.log(isDisplay, latestSystemNotification)
  //   // eslint-disable-next-line
  //   if(!isDisplay && user) {
  //     updateAllStatusNotification(latestSystemNotification?.id).then(({data}) => {
  //       if (data.success) {
  //         setCheckDisplay(false)
  //       } else {
  //         setCheckDisplay(true)
  //       }
  //     });
  //   }
  // }, [latestSystemNotification, user, updateAllStatusNotification]);

  useEffect(() => {
    setCurrentPath(location.pathname);
    // Remove localStorage by page
    if (!location.pathname.startsWith('/users')) {
      localStorage.removeItem('paramsQuery');
    }
    if (!location.pathname.startsWith('/training_progresses')) {
      localStorage.removeItem('paramsTrainingProgresses');
    }
  }, [location]);

  useEffect(() => {
    const onFocus = () => {
      const sessionChangedAt = localStorage.getItem('company_session_changed');
      const lastHandled = sessionStorage.getItem('company_session_changed_handled');
      if (sessionChangedAt && lastHandled !== sessionChangedAt) {
        sessionStorage.setItem('company_session_changed_handled', sessionChangedAt);
        COMPANY_API_RESET_ACTIONS.forEach((resetApiState) => {
          dispatch(resetApiState());
        });
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [dispatch]);

  const logout = useCallback(() => {
    COMPANY_API_RESET_ACTIONS.forEach((resetApiState) => {
      dispatch(resetApiState());
    });
    dispatch(resetAuthCompanyAction());
    localStorage.setItem('company_session_changed', String(Date.now()));
    navigate(RouteName.login);
  }, [dispatch, navigate]);

  const handleSignInOut = (shouldCheckRequiredTraining) => {
    if (!user) return navigate(RouteName.login);
    
    const isEditingRequiredTraining = JSON.parse(
      sessionStorage.getItem('isEditingRequiredTraining') || false,
    );

    if (shouldCheckRequiredTraining && isEditingRequiredTraining) {
      setShowConfirmLeaveRequiredTraining(true);
      setActionType('logout');
      return;
    }

   
    return logout();
  };
  const isActiveRoute = (routeName) => {
    return currentPath?.indexOf(routeName) !== -1;
  };

  const handleNavigate = useCallback(
    (routeName, actionType) => {
      const isEditingRequiredTraining = JSON.parse(
        sessionStorage.getItem('isEditingRequiredTraining') || false,
      );

      if (isEditingRequiredTraining) {
        setShowConfirmLeaveRequiredTraining(true);
        setActionType(actionType);
      } else {
        navigate(routeName);
      }
    },
    [navigate],
  );

  const handleConfirmLeaveRequiredTraining = useCallback(() => {
    setShowConfirmLeaveRequiredTraining(false);
    setActionType(null);
    sessionStorage.removeItem('isEditingRequiredTraining');

    switch (actionType) {
    case 'systemNotification':
      handleViewSystemNotification(false);
      break;
    case 'trainingReportCompany':
      navigate(RouteName.trainingReportCompany);
      break;
    case 'userList':
      navigate(RouteName.userList);
      break;
    case 'companyInfo':
      navigate(RouteName.companyInfo);
      break;
    case 'companyNotification':
      navigate(RouteName.companyNotification);
      break;
    case 'trainingCategoryList':
      navigate(RouteName.trainingCategoryList);
      break;
    case 'logout':
      logout();
      break;
    case 'termOfUse':
      navigate(RouteName.termOfUse);
      break;
    default:
      break;
    }
  }, [actionType, handleViewSystemNotification, logout, navigate]);

  return (
    <StyledSidebar>
      {campaignNotification && campaignNotification?.value && (
        <Box ml={18} mr={18} mt={20}>
          <Typography color="white" weight={600} breakLine={true}>
            {campaignNotification?.value}
            {campaignNotification?.extra?.url && (
              <Link
                href={campaignNotification?.extra?.url}
                target="_blank"
                underline="always"
                color="red"
              >
                こちら
              </Link>
            )}
          </Typography>
        </Box>
      )}
      <Box margin="32px 32px 18px 32px">
        <LogoIcon />
      </Box>
      <StyledSidebarItem
        active={isActiveRoute(RouteName.systemNotification)}
        onClick={() => handleViewSystemNotification(true)}
      >
        <Typography color="white" weight={600}>
          {t('sidebar:systemNotification')}
          {checkDisplay === true && (
            <span style={{ color: 'red' }}>{t('sidebar:labelNew')}</span>
          )}
        </Typography>
        <MenuNextIcon width="16" color="white" />
      </StyledSidebarItem>
      <StyledSidebarItem
        active={isActiveRoute(RouteName.trainingReportCompany)}
        onClick={() =>
          handleNavigate(
            RouteName.trainingReportCompany,
            'trainingReportCompany',
          )
        }
      >
        <Typography color="white" weight={600}>
          {t('sidebar:confirmTrainningReportBtn')}
        </Typography>
        <MenuNextIcon width="16" color="white" />
      </StyledSidebarItem>
      <StyledDivider></StyledDivider>
      <StyledSidebarItem
        active={isActiveRoute(RouteName.userList)}
        onClick={() => handleNavigate(RouteName.userList, 'userList')}
      >
        <Typography color="white" weight={600}>
          {t('sidebar:userManagementBtn')}
        </Typography>
        <MenuNextIcon width="16" color="white" />
      </StyledSidebarItem>
      <StyledDivider></StyledDivider>
      <StyledSidebarItem
        active={isActiveRoute(RouteName.companyInfo)}
        onClick={() => handleNavigate(RouteName.companyInfo, 'companyInfo')}
      >
        <Typography color="white" weight={600}>
          {!!(
            user &&
            user.company &&
            user.is_admin &&
            user.company.has_multiple_branches
          )
            ? t('sidebar:adminCompanyInfoBtn')
            : t('sidebar:companyInfoBtn')}
        </Typography>
        <MenuNextIcon width="16" color="white" />
      </StyledSidebarItem>
      <StyledDivider></StyledDivider>
      <StyledSidebarItem
        active={isActiveRoute(RouteName.companyNotification)}
        onClick={() =>
          handleNavigate(RouteName.companyNotification, 'companyNotification')
        }
      >
        <Typography color="white" weight={600}>
          {t('sidebar:companyNotificationBtn')}
        </Typography>
        <MenuNextIcon width="16" color="white" />
      </StyledSidebarItem>
      <StyledDivider></StyledDivider>
      {user && user.company && user.company.has_training_upload && (
        <>
          <StyledSidebarItem
            active={isActiveRoute(RouteName.trainingCategoryList)}
            onClick={() =>
              handleNavigate(
                RouteName.trainingCategoryList,
                'trainingCategoryList',
              )
            }
          >
            <Typography color="white" weight={600}>
              {t('sidebar:categoryListBtn')}
            </Typography>
            <MenuNextIcon width="16" color="white" />
          </StyledSidebarItem>
          <StyledDivider></StyledDivider>
        </>
      )}
      <StyledSidebarItem
        active={isActiveRoute(RouteName.requiredTraining)}
        onClick={() =>
          handleNavigate(RouteName.requiredTraining, 'requiredTraining')
        }
      >
        <Typography color="white" weight={600}>
          {t('sidebar:requiredVideo')}
        </Typography>
        <MenuNextIcon width="16" color="white" />
      </StyledSidebarItem>
      <StyledDivider></StyledDivider>
      <StyledButtonLogInOut onClick={() => handleSignInOut(true)} isLogin={!user}>
        {t(`sidebar:${user ? 'logoutBtn' : 'loginBtn'}`)}
      </StyledButtonLogInOut>
      <Box ml={32} mr={32} mt={20}>
        <Typography color="white" weight={600} breakLine={true}>
          {t('common:contactInfo:link')}
          <Link
            href="https://docs.google.com/forms/d/e/1FAIpQLSdOgVSkAtHS9XR_RYU2D_VhlCjNtVyqXvI9jXfqKxTWscD_nQ/viewform"
            target="_blank"
            underline="always"
            color="white"
          >
            こちら
          </Link>
        </Typography>
        <Typography color="white" weight={400}>
          {t('common:contactInfo:phone')}
        </Typography>
      </Box>
      <Box ml={32} mr={32} mt={20}>
        <Link
          onClick={() => handleNavigate(RouteName.termOfUse, 'termOfUse')}
          color="white"
          style={{ fontWeight: 600 }}
          underline="none"
        >
          {t('sidebar:terms')}
        </Link>
      </Box>
      {showConfirmLeaveRequiredTraining && (
        <ConfirmModal
          message={
            '必須研修が更新されていません！\n 今移動すると、必須研修が設定されません。\n必須研修を設定する場合は、\n最下部の【必要な研修を更新】をクリックしてください！'
          }
          cancelButtonLabel="設定を続ける"
          confirmButtonLabel="設定をやめる"
          onCancel={() => {
            setShowConfirmLeaveRequiredTraining(false);
            setActionType(null);
          }}
          onConfirm={handleConfirmLeaveRequiredTraining}
        />
      )}
    </StyledSidebar>
  );
});

export default Sidebar;
